import { gql, useQuery } from '@apollo/client';
import {
  Button,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Spinner,
} from '@apollo/orbit';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { graphSettingsRouteConfig } from 'src/app/graph/routes';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { escapeShellArgument } from 'src/lib/escapeShellArgument';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { notProduction } from 'src/lib/notProduction';
import { DocsRouter } from 'src/lib/routers';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { Code } from '../code/Code';
import { ErrorMessage } from '../errorMessage/ErrorMessage';
import { PermissionGuard } from '../permissionGuards/permissionGuard/PermissionGuard';

const setupMetricsQuery = gql`
  query SetupMetricsQuery($graphId: ID!) {
    service(id: $graphId) {
      id
      account {
        id
        availableRoles
      }
      apiKeys {
        id
        token
      }
    }
  }
`;

interface Props {
  graphRef: GraphRef;
  isOpen: boolean;
  onClose: () => void;
  __test?: {
    staging?: boolean;
  };
}

const STAGING_CODE = `import { ApolloServerPluginUsageReporting } from "apollo-server-core";
const server = new ApolloServer({
  typeDefs,
  resolvers,
  plugins: [
    ApolloServerPluginUsageReporting({
      endpointUrl: ${JSON.stringify(Config.stagingTracesEndpoint)}
    }),
  ],
});`;

/**
 * Show the first-time setup instructions for metrics
 */
export const SetUpMetrics = ({ graphRef, isOpen, onClose, __test }: Props) => {
  const { data, loading, error } = useQuery<
    GraphQLTypes.SetupMetricsQuery,
    GraphQLTypes.SetupMetricsQueryVariables
  >(setupMetricsQuery, {
    variables: {
      graphId: graphRef.graphId,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    accountId: currentAccountId as string,
    skip: !currentAccountId,
  });

  const history = useHistory();
  const location = useLocation();
  if (error || currentPlan.error) {
    throw error || currentPlan.error;
  }
  const apiKey = data?.service?.apiKeys?.[0]?.token;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Setup metrics</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {loading || currentPlan.loading ? (
            <div className="flex items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              <PermissionGuard
                graphId={graphRef.graphId}
                graphPermissions="canQueryTokens"
                fallback={
                  <ErrorMessage>
                    You do not have permission to view API keys for this graph.
                    Please contact one of your org or graph admins.
                  </ErrorMessage>
                }
              >
                {!apiKey && (
                  // user has permission to query tokens but still cannot see a key
                  <ErrorMessage>
                    We couldn't find an API key for your graph! Please navigate
                    to{' '}
                    <Link
                      to={graphSettingsRouteConfig.path({
                        graphId: graphRef.graphId,
                        graphVariant: graphRef.graphVariant,
                      })}
                    >
                      to the graph settings page
                    </Link>{' '}
                    to create a new one.
                  </ErrorMessage>
                )}
              </PermissionGuard>
              <p>
                Set up Apollo Server to get real-time analytics data flowing to
                Studio. Visit{' '}
                <Link to={DocsRouter.path('MonitoringAndMetrics')} isExternal>
                  Metrics
                </Link>{' '}
                in the Apollo Server docs for more information.
              </p>
              <p>
                Provide Apollo Server with your API key and graph variant via
                environment variables:
              </p>
              <Code
                className="flex-shrink-0"
                code={`// in your environment \nAPOLLO_KEY=${escapeShellArgument(
                  apiKey || '[Not Available]',
                )}\nAPOLLO_GRAPH_REF=${escapeShellArgument(
                  graphRef.graphId,
                )}@${escapeShellArgument(graphRef.graphVariant)}`}
              />
              <div>
                <Button
                  variant="secondary"
                  onClick={() => {
                    history.push(
                      setOverlayVisibleLocation({
                        location,
                        overlayName: Config.modals.createGraphApiToken,
                      }),
                    );
                  }}
                  aria-label={`Generate new token for ${'variant'}`}
                >
                  Generate new token
                </Button>
              </div>
              {(__test?.staging ?? notProduction) && (
                <>
                  <div className="mt-2">
                    Because this is the staging Studio, you'll also have to
                    configure the usage reporting plugin to talk to staging:
                  </div>
                  <Code
                    className="flex-shrink-0"
                    showLineNumbers={true}
                    code={STAGING_CODE}
                  />
                </>
              )}
              <p>
                Start your server, send it a GraphQL query, and metrics will
                begin to appear.
              </p>
              <div className="my-6 flex items-center justify-center">
                <Spinner size="tiny" variant="neutral" className="mr-2" />
                Awaiting metrics...
              </div>
              <div>
                <Link to={DocsRouter.path('UsageReporting')} isExternal>
                  See the docs
                </Link>{' '}
                to learn how to send metrics from any server.
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
